// 权益券种 Mock（对齐数据库权益券模块的券种表）
// 界面不暴露表名/字段名
//
// 发放规则的存储口径：
//   dispatch_ids   参与对象，**固定标识串**（monthly 全体用户 / milestone 达成用户，可并存如 "monthly,milestone"；空 = 仅手动发放）
//   dispatch_day            每月发放日（勾了「每月发放」时有效）
//   milestone_id   里程碑达成条件，字典 dispatch_milestone 的 _id（只有这项走字典，因为条件可运营增删）
//   milestone_mode   达成用户的发放方式：单独勾里程碑恒为 1（达成后一次性发）；
//                    每月+里程碑同勾时二选一：1 跟随每月发放日 / 2 每 milestone_interval_days 天周期发（从达成日起算）
//   milestone_num / dispatch_num  发放张数（券种级共享参数，两列同值）
//   结构：勾选=参与对象（可并存）；单独里程碑=一次性；双勾时达成用户才有 跟随/周期 radio；张数=独立参数
//   都不勾 = 仅手动发放；无论怎么配置，所有券都可以在后台「发放」按钮手动指定发放给用户
// 「每月发放 / 里程碑发放」是程序里的固定规则（代码要按它分支读参数），不进字典，选项在本文件写死

export const BENEFIT_CATEGORY = [
  { label: '旅居', value: 'stay' },
  { label: '景区', value: 'scenic' },
  { label: '活动', value: 'activity' },
  { label: '体验', value: 'experience' },
  { label: '通用', value: 'common' }
]

export const BENEFIT_STATUS = [
  { label: '启用', value: 1 },
  { label: '停用', value: 0 }
]

export const VALID_TYPE = [
  { label: '固定日期', value: 1 },
  { label: '发放后按天', value: 2 }
]

// 发放方式（固定两种，代码要按它分支读参数，故不进字典）
export const DISPATCH_OPTIONS = [
  { label: '每月发放', value: 'monthly' },
  { label: '里程碑发放', value: 'milestone' }
]

// 发放方式筛选下拉（多一项「手动发放」= 未配置任何自动发放，不是存储值）
export const DISPATCH_FILTER = [
  { label: '手动发放', value: 'manual' },
  { label: '每月发放', value: 'monthly' },
  { label: '里程碑发放', value: 'milestone' }
]

// 发放来源（用户券上的留痕，固定枚举：0 后台指定 / 1 每月发放 / 2 里程碑发放）
export const BENEFIT_SOURCE = [
  { label: '后台指定', value: 0 },
  { label: '每月发放', value: 1 },
  { label: '里程碑发放', value: 2 }
]

export function benefitSourceText(value) {
  const hit = BENEFIT_SOURCE.find(s => s.value === Number(value))
  return hit ? hit.label : '未记录'
}

export function benefitSourceTagType(value) {
  const v = Number(value)
  if (v === 1) return 'success'
  if (v === 2) return 'warning'
  return 'info'
}

// 发放方式标识串 → 标识数组（回显勾选、摘要判断共用）
export function splitDispatchValues(value) {
  const values = []
  String(value == null ? '' : value)
    .split(',')
    .forEach(s => {
      const v = s.trim()
      if (v !== '' && !values.some(x => x.value === v)) {
        const hit = DISPATCH_OPTIONS.find(d => d.value === v)
        if (hit) values.push(hit.value)
      }
    })
  return values
}

// 每月发放日（1-28 号，避开月末天数差异）
export const DISPATCH_DAYS = []
for (let i = 1; i <= 28; i++) DISPATCH_DAYS.push({ label: `${i} 号`, value: i })

export function benefitCategoryText(value) {
  const hit = BENEFIT_CATEGORY.find(c => c.value === value)
  return hit ? hit.label : (value || '未知')
}

export function benefitStatusText(status) {
  const hit = BENEFIT_STATUS.find(s => s.value === status)
  return hit ? hit.label : '未知'
}

export function benefitStatusTagType(status) {
  return status === 1 ? 'success' : 'info'
}

// 券类型标签配色（列表里做彩色小标签）
export function benefitCategoryTagType(value) {
  const map = { stay: 'success', scenic: '', activity: 'warning', experience: 'danger', common: 'info' }
  return map[value] === undefined ? 'info' : map[value]
}

// 字典项查找（按 _id 命中）
function dictItemOf(list, id) {
  return (list || []).find(it => Number(it._id) === Number(id)) || null
}

// 有效期文案：固定日期 / 发放后 N 天 / 长期有效
export function validText(row) {
  if (!row) return '—'
  if (row.valid_type === 3) return '长期有效'
  if (row.valid_type === 2) return `发放后 ${row.valid_days} 天内有效`
  if (row.start_date && row.end_date) return `${row.start_date} 至 ${row.end_date}`
  return '未设置'
}

// 发放规则摘要（按勾选组合生成；都没勾 = 手动发放）
// dicts：{ milestone: dispatch_milestone 字典项 }
export function dispatchSummary(row, dicts) {
  if (!row) return '—'
  const d = dicts || {}
  const values = splitDispatchValues(row.dispatch_ids)
  if (!values.length) return '手动发放'
  // 发放张数为券种级共享参数（每月/里程碑共用）：优先里程碑列，老数据回落每月列
  const shareNum = row.milestone_num || row.dispatch_num || 1
  const hasMonthly = values.indexOf('monthly') > -1
  const hasMilestone = values.indexOf('milestone') > -1
  const parts = []
  // 固定日期与周期互斥：达成用户选了周期发放时，全员每月固定日不生效
  if (hasMonthly && row.dispatch_day && !(hasMilestone && Number(row.milestone_mode) === 2)) {
    parts.push(`全体用户每月 ${row.dispatch_day} 日发 ${shareNum} 张`)
  }
  if (hasMilestone) {
    const m = dictItemOf(d.milestone, row.milestone_id)
    const cond = `达成「${m ? m.label : '未设置条件'}」用户`
    if (hasMonthly && Number(row.milestone_mode) === 2) {
      parts.push(`${cond}每 ${row.milestone_interval_days || 30} 天发 ${shareNum} 张`)
    } else if (hasMonthly) {
      parts.push(`${cond}跟随每月发放日`)
    } else {
      parts.push(`${cond}达成后发 ${shareNum} 张`)
    }
  }
  return parts.join('；')
}

// 每月发放：算下次发放日期（day = 每月发放日 1-28）
export function nextDispatchDate(day) {
  const d0 = Number(day || 0)
  if (!d0) return null
  const now = new Date()
  let d = new Date(now.getFullYear(), now.getMonth(), d0)
  if (d <= now) d = new Date(now.getFullYear(), now.getMonth() + 1, d0)
  return d
}

export function nextDispatchText(day, enabled) {
  if (!enabled) return '—'
  const d = nextDispatchDate(day)
  return d ? `下次 ${d.getMonth() + 1}月${d.getDate()}日` : '—'
}

// 发放量文案：总量 0 表示不限量
export function stockText(row) {
  if (!row) return '—'
  if (!row.total) return `不限量（已发 ${row.received_count}）`
  return `${row.received_count} / ${row.total}`
}

export function stockPercent(row) {
  if (!row || !row.total) return 0
  return Math.min(100, Math.round((row.received_count / row.total) * 100))
}

// 剩余可发放张数（不限量返回 Infinity）
export function grantRemaining(row) {
  if (!row) return 0
  if (!row.total) return Infinity
  return Math.max(0, row.total - row.received_count)
}

// 所有启用的券都可以在后台手动指定发放（指定发放不是券的类别）
export function canGrant(row) {
  return !!row
}

// 券封面占位图（票券样式示意稿，接入对象存储后由运营上传的真实封面覆盖）
function coverPlaceholder(theme) {
  const themes = {
    red: ['#8E1103', '#B8392C'],
    green: ['#2F6B5E', '#529C86'],
    gold: ['#8A6A24', '#C2A047'],
    blue: ['#2C4A72', '#527AA8'],
    brown: ['#6B5A45', '#9C876A']
  }
  const c = themes[theme] || themes.red
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="160" viewBox="0 0 240 160">
    <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="${c[0]}"/><stop offset="1" stop-color="${c[1]}"/>
    </linearGradient></defs>
    <rect width="240" height="160" rx="12" fill="url(#g)"/>
    <circle cx="200" cy="26" r="46" fill="#ffffff" opacity="0.10"/>
    <circle cx="36" cy="140" r="34" fill="#ffffff" opacity="0.08"/>
    <path d="M28 60h58" stroke="#ffffff" stroke-width="6" stroke-linecap="round" opacity="0.85"/>
    <path d="M28 84h88" stroke="#ffffff" stroke-width="5" stroke-linecap="round" opacity="0.55"/>
    <path d="M28 106h46" stroke="#ffffff" stroke-width="5" stroke-linecap="round" opacity="0.35"/>
    <g transform="translate(178,104)">
      <rect x="-28" y="-20" width="56" height="40" rx="7" fill="#ffffff" opacity="0.92"/>
      <text x="0" y="7" text-anchor="middle" font-size="21" font-family="sans-serif" fill="${c[0]}">券</text>
    </g>
  </svg>`
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
}

export const COVER_RED = coverPlaceholder('red')
export const COVER_GREEN = coverPlaceholder('green')
export const COVER_GOLD = coverPlaceholder('gold')
export const COVER_BLUE = coverPlaceholder('blue')
export const COVER_BROWN = coverPlaceholder('brown')

// 券种列表（mock 展示数据）
// dispatch_ids 是固定标识串；milestone_label 是 mock 专用文案（真接口只返里程碑字典 _id，中文走 /dict/map）
export const BENEFIT_LIST = [
  {
    id: 1, name: '桂林3日旅居权益券', category: 'stay', cover: COVER_GREEN,
    description: '桂林旅居基地 3 天 2 晚入住权益，含基地接待与一次周边游览',
    rules: '每张券限用一次，不可叠加使用\n入住前 7 天致电基地预约，凭核销码办理入住\n节假日期间需提前 15 天预约\n本券不兑现、不找零、不可转让',
    valid_type: 1, start_date: '2026-01-01', end_date: '2026-12-31', valid_days: 0,
    dispatch_ids: '', dispatch_day: 0, dispatch_num: 0, milestone_label: '',
    total: 500, received_count: 312, status: 1, created_at: '2026-01-05 10:20:00'
  },
  {
    id: 2, name: '圣山塔林景区畅游券', category: 'scenic', cover: COVER_RED,
    description: '圣山塔林景区大门票一张，含塔林核心区讲解服务',
    rules: '每张券限一人一次使用\n入园时出示核销码由工作人员扫码核销\n不包含景区内交通与餐饮消费',
    valid_type: 2, start_date: null, end_date: null, valid_days: 30,
    dispatch_ids: 'monthly', dispatch_day: 1, dispatch_num: 1, milestone_label: '',
    total: 0, received_count: 486, status: 1, created_at: '2026-02-11 14:36:00'
  },
  {
    id: 3, name: '塔林祈福体验券', category: 'experience', cover: COVER_GOLD,
    description: '塔林祈福仪式体验一次，含祈福牌一枚与现场讲解',
    rules: '每张券限一人一次使用\n需在景区开放时间内参与，每日限 60 人\n祈福牌款式以现场发放为准',
    valid_type: 2, start_date: null, end_date: null, valid_days: 15,
    dispatch_ids: 'milestone', dispatch_day: 0, dispatch_num: 0, milestone_label: '首次旅居入住',
    total: 0, received_count: 128, status: 1, created_at: '2026-03-02 09:48:00'
  },
  {
    id: 4, name: '春分祭祖活动券', category: 'activity', cover: COVER_BROWN,
    description: '春分祭祖典礼入场权益，含典礼观礼位与纪念册一份',
    rules: '仅限活动当日使用，过期自动失效\n每张券限一人入场，需实名登记\n现场听从工作人员引导，保持肃静',
    valid_type: 1, start_date: '2026-03-15', end_date: '2026-04-15', valid_days: 0,
    dispatch_ids: '', dispatch_day: 0, dispatch_num: 0, milestone_label: '',
    total: 200, received_count: 200, status: 0, created_at: '2026-03-06 16:12:00'
  },
  {
    id: 5, name: '家脉通用消费券', category: 'common', cover: COVER_BLUE,
    description: '家脉平台通用消费抵扣券，可用于旅居、景区与活动类目',
    rules: '每张券限用一次，不与其他优惠叠加\n抵扣不找零，剩余金额自动作废\n退款时券不退回',
    valid_type: 2, start_date: null, end_date: null, valid_days: 60,
    dispatch_ids: 'monthly,milestone', dispatch_day: 1, dispatch_num: 1, milestone_label: '邀请好友完成认证',
    total: 0, received_count: 745, status: 1, created_at: '2026-04-20 11:05:00'
  },
  {
    id: 6, name: '腾冲旅居体验券', category: 'stay', cover: COVER_GREEN,
    description: '腾冲旅居基地 2 天 1 晚体验权益，含接送站与温泉体验',
    rules: '每张券限用一次，不可叠加使用\n需提前 7 天致电基地预约\n接送站范围限腾冲市区及机场',
    valid_type: 1, start_date: '2026-06-01', end_date: '2026-09-30', valid_days: 0,
    dispatch_ids: 'milestone', dispatch_day: 0, dispatch_num: 0, milestone_label: '完成一次旅居行程',
    total: 0, received_count: 96, status: 0, created_at: '2026-05-18 15:30:00'
  }
]

// 表单初值：发放方式用 dispatchIdList（勾选的标识数组），提交时转成标识串
export function emptyBenefit() {
  return {
    id: 0, name: '', category: '', cover: '', description: '', rules: '',
    valid_type: 1, start_date: '', end_date: '', valid_days: 30,
    dispatchIdList: [], dispatch_day: 1, dispatch_num: 1, milestone_id: '',
    milestone_mode: 1, milestone_num: 1, milestone_interval_days: 30,
    total: 0, received_count: 0, status: 1, created_at: ''
  }
}

// 取一批券里可手动发放的（所有启用的券）
export function grantableBenefits() {
  return BENEFIT_LIST.filter(b => canGrant(b) && b.status === 1)
}
